import React, { useState, useEffect } from 'react';

interface DynamicCalendarIconProps {
  size?: 'small' | 'medium' | 'large';
  color?: 'red' | 'blue' | 'green' | 'orange' | 'purple';
  className?: string;
  onClick?: () => void;
}

export const DynamicCalendarIcon: React.FC<DynamicCalendarIconProps> = ({
  size = 'medium',
  color = 'blue',
  className = '',
  onClick
}) => {
  const [currentDate, setCurrentDate] = useState<number>(new Date().getDate());

  useEffect(() => {
    // Update date immediately
    updateDate();

    // Calculate milliseconds until next day at midnight
    const now = new Date();
    const msUntilMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
      0, 0, 0
    ).getTime() - now.getTime();

    // Set timeout for midnight, then interval for every 24 hours
    const midnightTimeout = setTimeout(() => {
      updateDate();
      
      // Set daily interval after midnight
      const dailyInterval = setInterval(updateDate, 24 * 60 * 60 * 1000);
      
      // Cleanup function will clear this interval
      return () => clearInterval(dailyInterval);
    }, msUntilMidnight);

    // Cleanup timeout on unmount
    return () => clearTimeout(midnightTimeout);
  }, []);

  const updateDate = () => {
    setCurrentDate(new Date().getDate());
  };

  // Size configurations
  const sizeConfig = {
    small: {
      container: 'w-6 h-6',
      date: 'text-xs',
      ring: 'w-4 h-1',
      holes: 'w-0.5 h-1.5 left-1.5',
      holesShadow: '3px 0 0 hsl(var(--muted-foreground))'
    },
    medium: {
      container: 'w-8 h-8',
      date: 'text-sm',
      ring: 'w-5 h-1',
      holes: 'w-0.5 h-2 left-2',
      holesShadow: '4px 0 0 hsl(var(--muted-foreground))'
    },
    large: {
      container: 'w-10 h-10',
      date: 'text-base',
      ring: 'w-6 h-1.5',
      holes: 'w-1 h-2.5 left-2.5',
      holesShadow: '5px 0 0 hsl(var(--muted-foreground))'
    }
  };

  // Color configurations
  const colorConfig = {
    red: 'bg-red-500 shadow-red-500/30 hover:shadow-red-500/40',
    blue: 'bg-primary shadow-primary/30 hover:shadow-primary/40',
    green: 'bg-green-500 shadow-green-500/30 hover:shadow-green-500/40',
    orange: 'bg-orange-500 shadow-orange-500/30 hover:shadow-orange-500/40',
    purple: 'bg-purple-500 shadow-purple-500/30 hover:shadow-purple-500/40'
  };

  const config = sizeConfig[size];

  return (
    <div
      className={`
        relative inline-block ${config.container} ${colorConfig[color]}
        rounded-lg cursor-pointer transition-all duration-300 ease-in-out
        shadow-lg hover:shadow-xl hover:-translate-y-0.5
        ${className}
      `}
      onClick={onClick}
      style={{
        // Custom CSS for precise calendar styling
        background: color === 'red' ? '#ef4444' : 
                   color === 'blue' ? '#3b82f6' : 
                   color === 'green' ? '#10b981' : 
                   color === 'orange' ? '#f97316' : 
                   color === 'purple' ? '#8b5cf6' : '#3b82f6'
      }}
    >
      {/* Calendar Ring (top spiral binding) */}
      <div
        className={`
          absolute -top-0.5 left-1/2 transform -translate-x-1/2
          ${config.ring} bg-gray-700 rounded-sm
        `}
      />
      
      {/* Calendar Holes (binding holes) */}
      <div
        className={`
          absolute top-0 ${config.holes} bg-gray-700 rounded-sm
        `}
        style={{
          boxShadow: config.holesShadow
        }}
      />
      
      {/* Date Number */}
      <div
        className={`
          absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
          ${config.date} font-bold text-white font-sans select-none
        `}
        style={{
          textShadow: '0 1px 2px rgba(0,0,0,0.1)',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
        }}
      >
        {currentDate}
      </div>
    </div>
  );
};